import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { storageBoxApi } from '../../../storageBox/api/storageBoxApi';
import { fetchAndDecryptLockFile, revokeObjectUrl } from '../../../storageBox/utils/lockFileAccess';
import { useStorageBox } from '../../../storageBox/StorageBoxContext';
import AudioPlayer from './AudioPlayer';
import { isPreviewableAudio } from './audioFormatUtils';

function useDocumentThemeMode() {
  const [mode, setMode] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark'
      ? 'dark'
      : 'light'
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const observer = new MutationObserver(() => {
      setMode(document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return mode;
}

/**
 * Audio player wired for Storage Box items (stream URL, lock decrypt, offline download).
 */
export default function StorageBoxAudioPlayer({
  item,
  publicToken = null,
  compact = true,
  className = '',
  onQueueAdvance,
  onPlayStateChange,
  playerApiRef = null,
  queueHasNextTrack = false,
  autoPlayRequest = 0,
  autoPlayOnSrcChange = true,
  equalizerGains = null,
  onEqualizerGainsChange = null,
  onEqualizerPanelChange = null,
  onWindowMinimize = null,
  onWindowClose = null,
}) {
  const themeMode = useDocumentThemeMode();
  const {
    lockSessionToken,
    inLockVault,
    getLockMasterKeyBytes,
    lockMasterKeyReady,
    notify,
  } = useStorageBox();

  const [streamUrl, setStreamUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mergedItem = item;
  const isAudio = mergedItem?.type === 'file' && isPreviewableAudio(mergedItem.mimeType, mergedItem.name);

  useEffect(() => {
    let revoke = null;
    let cancelled = false;

    async function resolve() {
      setStreamUrl('');
      setError('');
      if (!mergedItem?.itemId || !isAudio) return;

      setLoading(true);
      try {
        if (inLockVault && mergedItem.isEncrypted && lockSessionToken) {
          if (!lockMasterKeyReady) {
            setError('Unlock Lock folder to play this file.');
            return;
          }
          const masterKey = getLockMasterKeyBytes();
          if (!masterKey) {
            setError('Unlock Lock folder to play this file.');
            return;
          }
          const { blob } = await fetchAndDecryptLockFile(mergedItem, lockSessionToken, masterKey);
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          revoke = url;
          setStreamUrl(url);
          return;
        }

        const url = storageBoxApi.downloadUrl(mergedItem.itemId, {
          publicToken,
          lockSessionToken:
            inLockVault && lockSessionToken && mergedItem.isEncrypted ? lockSessionToken : null,
        });
        if (!cancelled) setStreamUrl(url);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Could not prepare audio');
          notify?.(e.message || 'Could not prepare audio');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    resolve();
    return () => {
      cancelled = true;
      revokeObjectUrl(revoke);
    };
  }, [
    mergedItem,
    isAudio,
    publicToken,
    inLockVault,
    lockSessionToken,
    lockMasterKeyReady,
    getLockMasterKeyBytes,
    notify,
  ]);

  useEffect(() => {
    if (!streamUrl || !autoPlayRequest) return undefined;
    let cancelled = false;
    const attempt = () => {
      if (cancelled) return;
      const api = playerApiRef?.current;
      if (api?.play) {
        api.play().catch(() => {});
        return;
      }
      requestAnimationFrame(attempt);
    };
    requestAnimationFrame(attempt);
    return () => {
      cancelled = true;
    };
  }, [streamUrl, autoPlayRequest, playerApiRef]);

  const fetchBlob = useCallback(async () => {
    if (inLockVault && mergedItem?.isEncrypted && lockSessionToken) {
      const masterKey = getLockMasterKeyBytes();
      if (!masterKey) throw new Error('Unlock Lock folder to download this file.');
      const { blob } = await fetchAndDecryptLockFile(mergedItem, lockSessionToken, masterKey);
      return blob;
    }
    const url = storageBoxApi.downloadUrl(mergedItem.itemId, { publicToken });
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Download failed');
    return res.blob();
  }, [mergedItem, inLockVault, lockSessionToken, getLockMasterKeyBytes, publicToken]);

  const offlineKey = useMemo(
    () => (mergedItem?.itemId ? `sb:${mergedItem.itemId}` : ''),
    [mergedItem?.itemId]
  );

  if (!isAudio) return null;

  if (loading && !streamUrl) {
    return <p className="audio-player__message">Preparing audio…</p>;
  }

  if (error && !streamUrl) {
    return (
      <p className="audio-player__message audio-player__message--error" role="alert">
        {error}
      </p>
    );
  }

  return (
    <AudioPlayer
      className={className}
      compact={compact}
      theme={themeMode}
      src={streamUrl}
      title={mergedItem.name}
      subtitle="Storage Box"
      mimeType={mergedItem.mimeType}
      offlineKey={offlineKey}
      onFetchBlob={fetchBlob}
      onQueueAdvance={onQueueAdvance}
      onPlayStateChange={onPlayStateChange}
      playerApiRef={playerApiRef}
      queueHasNextTrack={queueHasNextTrack}
      autoPlayRequest={autoPlayRequest}
      autoPlayOnSrcChange={autoPlayOnSrcChange}
      equalizerGains={equalizerGains}
      onEqualizerGainsChange={onEqualizerGainsChange}
      onEqualizerPanelChange={onEqualizerPanelChange}
      onWindowMinimize={onWindowMinimize}
      onWindowClose={onWindowClose}
    />
  );
}

/**
 * Standalone player for a Storage Box file (product page — no StorageBoxContext).
 */
export function StorageBoxFileAudioPlayer({
  fileItem,
  className = '',
  compact = false,
  onQueueAdvance,
  onPlayStateChange,
  playerApiRef = null,
  queueHasNextTrack = false,
  autoPlayRequest = 0,
  autoPlayOnSrcChange = true,
  equalizerGains = null,
  onEqualizerGainsChange = null,
  onEqualizerPanelChange = null,
  onWindowMinimize = null,
  onWindowClose = null,
}) {
  const themeMode = useDocumentThemeMode();
  const [streamUrl, setStreamUrl] = useState('');

  useEffect(() => {
    if (!fileItem?.itemId) {
      setStreamUrl('');
      return undefined;
    }
    setStreamUrl(storageBoxApi.downloadUrl(fileItem.itemId));
    return undefined;
  }, [fileItem?.itemId]);

  useEffect(() => {
    if (!streamUrl || !autoPlayRequest) return undefined;
    let cancelled = false;
    const attempt = () => {
      if (cancelled) return;
      const api = playerApiRef?.current;
      if (api?.play) {
        api.play().catch(() => {});
        return;
      }
      requestAnimationFrame(attempt);
    };
    requestAnimationFrame(attempt);
    return () => {
      cancelled = true;
    };
  }, [streamUrl, autoPlayRequest, playerApiRef]);

  const fetchBlob = useCallback(async () => {
    const url = storageBoxApi.downloadUrl(fileItem.itemId);
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Download failed');
    return res.blob();
  }, [fileItem?.itemId]);

  if (!fileItem) return null;

  return (
    <AudioPlayer
      className={className}
      compact={compact}
      theme={themeMode}
      src={streamUrl}
      title={fileItem.name}
      subtitle="Storage Box"
      mimeType={fileItem.mimeType}
      offlineKey={`sb:${fileItem.itemId}`}
      onFetchBlob={fetchBlob}
      onQueueAdvance={onQueueAdvance}
      onPlayStateChange={onPlayStateChange}
      playerApiRef={playerApiRef}
      queueHasNextTrack={queueHasNextTrack}
      autoPlayRequest={autoPlayRequest}
      autoPlayOnSrcChange={autoPlayOnSrcChange}
      equalizerGains={equalizerGains}
      onEqualizerGainsChange={onEqualizerGainsChange}
      onEqualizerPanelChange={onEqualizerPanelChange}
      onWindowMinimize={onWindowMinimize}
      onWindowClose={onWindowClose}
    />
  );
}
